import {
    BrowserRouter as Router,
    Routes,
    Route,
    Navigate,
} from "react-router-dom";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { CssBaseline } from "@mui/material";
import { ReduxProvider } from "./store/ReduxProvider";
import { AuthProvider } from "./contexts/AuthContext";
import { ProtectedRoute } from "./components/common/ProtectedRoute";
import { OwnerRoute } from "./components/common/OwnerRoute";
import { RequireArea } from "./components/common/RequireArea";
import { Layout } from "./components/layout/Layout";
import { AuthPage } from "./components/auth/AuthPage";
import { ResetPasswordPage } from "./components/auth/ResetPasswordPage";
import { Dashboard } from "./pages/Dashboard";
import { ProductList } from "./pages/products/ProductList";
import { ProductMerge } from "./pages/products/ProductMerge";
import { ArrangeShop } from "./pages/products/ArrangeShop";
import { CampaignList } from "./pages/campaigns/CampaignList";
import { CampaignEditor } from "./pages/campaigns/CampaignEditor";
import { CategoryList } from "./pages/categories/CategoryList";
import { BrandList } from "./pages/brands/BrandList";
import { SupplierList } from "./pages/suppliers/SupplierList";
import { CustomerList } from "./pages/customers/CustomerList";
import { DuplicateCustomers } from "./pages/customers/DuplicateCustomers";
import { ContactMessages } from "./pages/customers/ContactMessages";
import { OrderList } from "./pages/orders/OrderList";
import { GuestOrderRequests } from "./pages/orders/GuestOrderRequests";
import { OrderStatistics } from "./pages/orders/OrderStatistics";
import { ExpenseList } from "./pages/expenses/ExpenseList";
import { ExpenseStatistics } from "./pages/expenses/ExpenseStatistics";
import { StockManagement } from "./pages/inventory/StockManagement";
import ConversionList from "./pages/inventory/ConversionList";
import CreateConversion from "./pages/inventory/CreateConversion";
import ViewConversion from "./pages/inventory/ViewConversion";
import { POSSystem } from "./pages/pos/POSSystem";
import { UserApproval } from "./pages/admin/UserApproval";
import { ToolsDashboard } from "./pages/tools/ToolsDashboard";
import { PriceListGenerator } from "./pages/tools/PriceListGenerator";
import { DeliveryPlanner } from "./pages/tools/DeliveryPlanner";
import { DailyCashFlowAnalyzer } from "./pages/tools/DailyCashFlowAnalyzer";
import ProcurementDashboard from "./pages/procurement/ProcurementDashboard";
import SupplierListProcurement from "./pages/procurement/SupplierList";
import PurchaseOrderList from "./pages/procurement/PurchaseOrderList";
import CreatePurchaseOrder from "./pages/procurement/CreatePurchaseOrder";
import EditPurchaseOrder from "./pages/procurement/EditPurchaseOrder";
import ViewPurchaseOrder from "./pages/procurement/ViewPurchaseOrder";
import { Settings } from "./pages/settings/Settings";
import { DeliveryServiceManagement } from "./pages/settings/DeliveryServiceManagement";
import { DeliveryAreaManagement } from "./pages/settings/DeliveryAreaManagement";
import { OrderChannelManagement } from "./pages/settings/OrderChannelManagement";
import { ExpenseCategoryManagement } from "./pages/settings/ExpenseCategoryManagement";
import { ProfitLoss } from "./pages/reports/ProfitLoss";
import { ShopAnalytics } from "./pages/reports/ShopAnalytics";
import { AssetList } from "./pages/assets/AssetList";
import { AccountList } from "./pages/accounts/AccountList";
import { PaymentsList } from "./pages/payments/PaymentsList";
import { AnswerDesk } from "./pages/answers/AnswerDesk";
import { AnswerReview } from "./pages/answers/AnswerReview";
import { TasksPage } from "./pages/tasks/TasksPage";

const theme = createTheme({
    palette: {
        primary: {
            main: "#18383E",
        },
        secondary: {
            main: "#dc004e",
        },
    },
    components: {
        // Tables: "Rows per page" and the page arrows sit bottom-left —
        // wide tables scroll sideways, and on the right they were often
        // off-screen. (The pager's spacer is what pushed them right.)
        MuiTablePagination: {
            styleOverrides: {
                spacer: { display: "none" },
            },
        },
    },
});

function App() {
    return (
        <ReduxProvider>
            <ThemeProvider theme={theme}>
                <CssBaseline />
                <AuthProvider>
                    <Router>
                        <Routes>
                            <Route path="/login" element={<AuthPage />} />
                            <Route
                                path="/reset-password"
                                element={<ResetPasswordPage />}
                            />
                            {/* Standalone Statistics Pages - No Layout */}
                            <Route
                                path="/orders/statistics"
                                element={
                                    <ProtectedRoute>
                                        <OrderStatistics />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/expenses/statistics"
                                element={
                                    <ProtectedRoute>
                                        <RequireArea area="operations">
                                            <ExpenseStatistics />
                                        </RequireArea>
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/*"
                                element={
                                    <ProtectedRoute>
                                        <Layout>
                                            <Routes>
                                                <Route
                                                    path="/"
                                                    element={
                                                        <Navigate
                                                            to="/pos"
                                                            replace
                                                        />
                                                    }
                                                />
                                                <Route
                                                    path="/dashboard"
                                                    element={<Dashboard />}
                                                />
                                                <Route
                                                    path="/pos"
                                                    element={
                                                        <RequireArea area="operations">
                                                            <POSSystem />
                                                        </RequireArea>
                                                    }
                                                />
                                                <Route
                                                    path="/products"
                                                    element={<ProductList />}
                                                />
                                                <Route
                                                    path="/products/merge"
                                                    element={<ProductMerge />}
                                                />
                                                <Route
                                                    path="/products/arrange"
                                                    element={<ArrangeShop />}
                                                />
                                                <Route
                                                    path="/campaigns"
                                                    element={<CampaignList />}
                                                />
                                                {/* "new" and an id share one route, so creating
                                                    a campaign moves to its address in place. */}
                                                <Route
                                                    path="/campaigns/:id"
                                                    element={<CampaignEditor />}
                                                />
                                                <Route
                                                    path="/categories"
                                                    element={<CategoryList />}
                                                />
                                                <Route
                                                    path="/brands"
                                                    element={<BrandList />}
                                                />
                                                <Route
                                                    path="/suppliers"
                                                    element={
                                                        <RequireArea area="operations">
                                                            <SupplierList />
                                                        </RequireArea>
                                                    }
                                                />
                                                <Route
                                                    path="/customers"
                                                    element={<CustomerList />}
                                                />
                                                <Route
                                                    path="/customers/duplicates"
                                                    element={
                                                        <DuplicateCustomers />
                                                    }
                                                />
                                                <Route
                                                    path="/customers/messages"
                                                    element={
                                                        <RequireArea area="operations">
                                                            <ContactMessages />
                                                        </RequireArea>
                                                    }
                                                />
                                                <Route
                                                    path="/answers"
                                                    element={
                                                        <RequireArea area="operations">
                                                            <AnswerDesk />
                                                        </RequireArea>
                                                    }
                                                />
                                                <Route
                                                    path="/answers/review"
                                                    element={
                                                        <RequireArea area="operations">
                                                            <AnswerReview />
                                                        </RequireArea>
                                                    }
                                                />
                                                <Route
                                                    path="/tasks"
                                                    element={
                                                        <RequireArea area="operations">
                                                            <TasksPage />
                                                        </RequireArea>
                                                    }
                                                />
                                                <Route
                                                    path="/orders"
                                                    element={<OrderList />}
                                                />
                                                <Route
                                                    path="/orders/requests"
                                                    element={
                                                        <RequireArea area="operations">
                                                            <GuestOrderRequests />
                                                        </RequireArea>
                                                    }
                                                />
                                                <Route
                                                    path="/expenses"
                                                    element={
                                                        <RequireArea area="operations">
                                                            <ExpenseList />
                                                        </RequireArea>
                                                    }
                                                />
                                                <Route
                                                    path="/assets"
                                                    element={
                                                        <RequireArea area="operations">
                                                            <AssetList />
                                                        </RequireArea>
                                                    }
                                                />
                                                <Route
                                                    path="/accounts"
                                                    element={
                                                        <RequireArea area="financial_reports">
                                                            <AccountList />
                                                        </RequireArea>
                                                    }
                                                />
                                                <Route
                                                    path="/payments"
                                                    element={
                                                        <RequireArea area="financial_reports">
                                                            <PaymentsList />
                                                        </RequireArea>
                                                    }
                                                />
                                                <Route
                                                    path="/tools"
                                                    element={
                                                        <RequireArea area="operations">
                                                            <ToolsDashboard />
                                                        </RequireArea>
                                                    }
                                                />
                                                <Route
                                                    path="/tools/price-list-generator"
                                                    element={
                                                        <RequireArea area="operations">
                                                            <PriceListGenerator />
                                                        </RequireArea>
                                                    }
                                                />
                                                <Route
                                                    path="/tools/delivery-planner"
                                                    element={
                                                        <RequireArea area="operations">
                                                            <DeliveryPlanner />
                                                        </RequireArea>
                                                    }
                                                />
                                                <Route
                                                    path="/tools/daily-cash-flow-analyzer"
                                                    element={
                                                        <RequireArea area="operations">
                                                            <DailyCashFlowAnalyzer />
                                                        </RequireArea>
                                                    }
                                                />
                                                <Route
                                                    path="/inventory/stock"
                                                    element={
                                                        <StockManagement />
                                                    }
                                                />
                                                <Route
                                                    path="/inventory/conversions"
                                                    element={
                                                        <ConversionList />
                                                    }
                                                />
                                                <Route
                                                    path="/inventory/conversions/create"
                                                    element={
                                                        <CreateConversion />
                                                    }
                                                />
                                                <Route
                                                    path="/inventory/conversions/:id/view"
                                                    element={
                                                        <ViewConversion />
                                                    }
                                                />
                                                {/* Procurement Routes */}
                                                <Route
                                                    path="/procurement"
                                                    element={
                                                        <RequireArea area="operations">
                                                            <ProcurementDashboard />
                                                        </RequireArea>
                                                    }
                                                />
                                                <Route
                                                    path="/procurement/dashboard"
                                                    element={
                                                        <RequireArea area="operations">
                                                            <ProcurementDashboard />
                                                        </RequireArea>
                                                    }
                                                />
                                                <Route
                                                    path="/procurement/suppliers"
                                                    element={
                                                        <RequireArea area="operations">
                                                            <SupplierListProcurement />
                                                        </RequireArea>
                                                    }
                                                />
                                                <Route
                                                    path="/procurement/suppliers/create"
                                                    element={
                                                        <RequireArea area="operations">
                                                            <SupplierListProcurement />
                                                        </RequireArea>
                                                    }
                                                />
                                                <Route
                                                    path="/procurement/purchases"
                                                    element={
                                                        <RequireArea area="operations">
                                                            <PurchaseOrderList />
                                                        </RequireArea>
                                                    }
                                                />
                                                <Route
                                                    path="/procurement/purchases/create"
                                                    element={
                                                        <RequireArea area="operations">
                                                            <CreatePurchaseOrder />
                                                        </RequireArea>
                                                    }
                                                />
                                                <Route
                                                    path="/procurement/purchases/:id/view"
                                                    element={
                                                        <RequireArea area="operations">
                                                            <ViewPurchaseOrder />
                                                        </RequireArea>
                                                    }
                                                />
                                                <Route
                                                    path="/procurement/purchases/:id/edit"
                                                    element={
                                                        <RequireArea area="operations">
                                                            <EditPurchaseOrder />
                                                        </RequireArea>
                                                    }
                                                />
                                                <Route
                                                    path="/procurement/quality-check"
                                                    element={
                                                        <RequireArea area="operations">
                                                            <PurchaseOrderList />
                                                        </RequireArea>
                                                    }
                                                />
                                                <Route
                                                    path="/procurement/batches/expiring"
                                                    element={
                                                        <RequireArea area="operations">
                                                            <PurchaseOrderList />
                                                        </RequireArea>
                                                    }
                                                />
                                                <Route
                                                    path="/reports/profit-loss"
                                                    element={<ProfitLoss />}
                                                />
                                                <Route
                                                    path="/reports/shop-analytics"
                                                    element={<ShopAnalytics />}
                                                />
                                                <Route
                                                    path="/settings"
                                                    element={
                                                        <RequireArea area="operations">
                                                            <Settings />
                                                        </RequireArea>
                                                    }
                                                />
                                                <Route
                                                    path="/settings/delivery-services"
                                                    element={
                                                        <RequireArea area="operations">
                                                            <DeliveryServiceManagement />
                                                        </RequireArea>
                                                    }
                                                />
                                                <Route
                                                    path="/settings/delivery-areas"
                                                    element={
                                                        <RequireArea area="operations">
                                                            <DeliveryAreaManagement />
                                                        </RequireArea>
                                                    }
                                                />
                                                <Route
                                                    path="/settings/order-channels"
                                                    element={
                                                        <RequireArea area="operations">
                                                            <OrderChannelManagement />
                                                        </RequireArea>
                                                    }
                                                />
                                                <Route
                                                    path="/settings/expense-categories"
                                                    element={
                                                        <RequireArea area="operations">
                                                            <ExpenseCategoryManagement />
                                                        </RequireArea>
                                                    }
                                                />
                                                <Route
                                                    path="/admin/user-approval"
                                                    element={
                                                        <OwnerRoute>
                                                            <UserApproval />
                                                        </OwnerRoute>
                                                    }
                                                />
                                                <Route
                                                    path="*"
                                                    element={
                                                        <Navigate
                                                            to="/dashboard"
                                                            replace
                                                        />
                                                    }
                                                />
                                            </Routes>
                                        </Layout>
                                    </ProtectedRoute>
                                }
                            />
                        </Routes>
                    </Router>
                </AuthProvider>
            </ThemeProvider>
        </ReduxProvider>
    );
}

export default App;
